import React, { FC, useCallback, useEffect, useRef } from 'react';

import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useCurrentEmployee } from 'Src/feature/employee/useCurrentEmployee';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { useOperator } from 'Src/feature/operator/useOperator';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useMountedState } from 'Src/feature/use-mounted/useMountedState';

import { useSocket } from './useSocket';
import { SocketAutoRetrierProvider } from './useSocketRetryOnReconnect';

let mountedCount = 0;
const sectionId = 'ReconnectionHandler';

/**
 * ReconnectionHandler monitors to see if our socket connection dropped then reconnected.
 * If it did, then it will await the terminal svc to re-initialize (which it automatically does on reconnection),
 * and then it will check to see if we had an Operator at the helm. If we did, it will auto-log in.
 * This should only be used once and at the root of the app.
 */
const ReconnectionHandler: FC = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const socket = useSocket();
  const { isUnMounted } = useMountedState();
  const isHandlingReconnect = useRef(false);
  const terminalLocation = useTerminal.query.get();
  const isReady = useTerminal.isReady();
  const employee = useCurrentEmployee();
  const checkHeadersInvalidate = useCheckHeader.query.invalidate();
  const employeeInvalidate = useEmployee.query.invalidate();
  const login = useOperator.mutate.login();

  const handleReconnect = useCallback(async () => {
    if (!isReady) return;
    isHandlingReconnect.current = true;
    try {
      await terminalLocation.refetch({ throwOnError: true });
      if (isUnMounted) return;
      if (employee) {
        // !!! invalidate large data collections that might have missed notifications while we were down
        checkHeadersInvalidate.all();
        employeeInvalidate.all();

        await login.mutateAsync({
          authNRequest: {
            pin: employee.pin,
            cardCode: employee.cardCode,
          },
        });
      }
      if (isUnMounted) return;
      isHandlingReconnect.current = false;
    } catch (err) {
      log.error('handleReconnect', err);
    }
  }, [checkHeadersInvalidate, employee, employeeInvalidate, isReady, isUnMounted, log, login, terminalLocation]);

  // mount/unmount handler
  useEffect(() => {
    if (mountedCount > 0) {
      throw new Error(`${sectionId} should only be used one time`);
    }
    mountedCount += 1;
    return () => {
      mountedCount -= 1;
    };
  }, []);

  // socket subscription handler (socket ref won't change as it's a singleton)
  useEffect(() => {
    // b/c handleReconnect is an async function, it thinks I should put `void` in front
    // however if I do that it complains that void is not assignable to the callback type
    socket.on('reconnection', handleReconnect as () => void);

    return () => {
      socket.off('reconnection', handleReconnect as () => void);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleReconnect]);

  return (
    <SocketAutoRetrierProvider
      value={{
        socket,
        authMonitor: {
          get isReAuthInProgress(): boolean {
            return isHandlingReconnect.current;
          },
        },
      }}
    />
  );
};

export { ReconnectionHandler };
